import { ClaimDetailed, ActivityEvent, User } from "@/api/generated/client/models";
import { useChatConversation } from "@/hooks/use-chat-conversation";
import { ChatMessagesList } from "./chat/ChatMessagesList";
import { ChatInput } from "./chat/ChatInput";
import { ChatEmptyState } from "./chat/ChatEmptyState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface TurboAIChatProps {
    claimId: number;
    claim: ClaimDetailed;
    activities: ActivityEvent[];
    currentUser?: User | null;
    canWrite: boolean;
}

export default function TurboAIChat({ claimId, canWrite }: TurboAIChatProps) {
    const { toast } = useToast();
    const [sendError, setSendError] = useState<string | null>(null);
    
    const {
        conversation,
        messages,
        isLoading,
        isError,
        error,
        sendMessage,
        isSending,
        createConversation,
        isCreating,
        startNewConversation,
    } = useChatConversation({ claimId });

    const hasMessages = messages.length > 0;
    const hasConversation = conversation !== null;

    const handleSendMessage = async (content: string) => {
        if (!canWrite) return;
        try {
            setSendError(null);
            await sendMessage(content);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
            console.error('Failed to send message:', err);
            setSendError(errorMessage);
            
            // Show toast notification
            toast({
                variant: "destructive",
                title: "Message Failed",
                description: "Your message could not be sent. Please try again.",
            });
        }
    };

    const handleStartConversation = async () => {
        if (!canWrite) return;
        try {
            await createConversation();
        } catch (err) {
            console.error('Failed to create conversation:', err);
            toast({
                variant: "destructive",
                title: "Failed to Start Conversation",
                description: "Could not create a new conversation. Please try again.",
            });
        }
    };

    const handleNewConversation = async () => {
        if (!canWrite) return;
        try {
            await startNewConversation();
            toast({
                title: "New Conversation Started",
                description: "You can now start a fresh conversation.",
            });
        } catch (err) {
            console.error('Failed to start new conversation:', err);
            toast({
                variant: "destructive",
                title: "Failed to Start New Conversation",
                description: "Could not create a new conversation. Please try again.",
            });
        }
    };

    if (isError) {
        return (
            <div className="flex flex-col h-full items-center justify-center p-4">
                <Alert variant="destructive" className="max-w-md">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        {error?.message || 'Failed to load chat conversation'}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-background w-full max-w-full overflow-hidden">
            {/* Header with TurboAI Chat title */}
            <div className="px-6 pt-6 pb-4 border-b">
                <div>
                    <h2 className="text-xl font-semibold">TurboAI Chat</h2>
                    <p className="text-sm text-muted-foreground mt-1">Ask questions about this claim</p>
                </div>
            </div>

            {!hasConversation && !isLoading && !isCreating ? (
                <ChatEmptyState 
                    onStartConversation={handleStartConversation}
                    isCreating={isCreating}
                    disabled={!canWrite}
                />
            ) : (
                <>
                    
                    {sendError && (
                        <div className="px-4 pt-4">
                            <Alert variant="destructive" className="mb-0">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="flex items-center justify-between">
                                    <span>{sendError}</span>
                                    <button
                                        onClick={() => setSendError(null)}
                                        className="text-xs underline hover:no-underline"
                                    >
                                        Dismiss
                                    </button>
                                </AlertDescription>
                            </Alert>
                        </div>
                    )}
                    <ChatMessagesList 
                        messages={messages}
                        isLoading={isLoading && !hasMessages}
                        isSending={isSending}
                    />
                    
                    {/* New Chat button in messages area */}
                    {hasMessages && (
                        <div className="px-4 pb-2 flex justify-center">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleNewConversation}
                                disabled={isCreating || !canWrite}
                                className="gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Start New Chat
                            </Button>
                        </div>
                    )}
                    
                    <ChatInput
                        onSend={handleSendMessage}
                        disabled={!hasConversation || !canWrite}
                        isLoading={isSending}
                        placeholder={
                            hasMessages 
                                ? "Continue the conversation..." 
                                : "Ask TurboAI anything about this claim..."
                        }
                    />
                </>
            )}
        </div>
    );
}
